import {
  Box,
  Button,
  Center,
  CircularProgress,
  Collapse,
  Flex,
  HStack,
  Image,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverProps,
  PopoverTrigger,
  Portal,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import {
  Assessment,
  CheckboxField,
  colors,
  FieldMap,
  FieldTypes,
  FileDBValue,
  getAssessmentFilesDir,
  getPatientFileField,
  getPatientFilesField,
  OnUploadProgress,
  PracticeAssessmentData,
  TextField,
  ThreadType,
  UploadProgress,
  UserGroup,
} from '@hb/shared'
import { FORM_ERROR } from 'final-form'
import { DocumentReference, writeBatch } from 'firebase/firestore'

import { useFirebase } from '@chem-po/firebase-web'
import { addMetadata, PopUpMessageContext, ScreenContext, useApp, useAuth } from '@hb/react'
import { useCallback, useContext, useMemo, useState } from 'react'
import { db } from '../../../../backend/db/db'
import { processFileFieldData } from '../../../../backend/db/utils/processData'
import { combineFilesIntoPdf } from '../../../../backend/functions/other'
import { useProfile } from '../../../../contexts/user/ProfileContext'
import uploadIcon from '../../../../icons/upload.svg'
import { SimpleForm } from '../../../forms'
import { sendThreadMessage } from '../../../Messaging/utils'
import { useProfilePopoverMaxHeight } from '../hooks/useProfilePopoverMaxHeight'
import { ShouldNotify } from '../ShouldNotify'

const combineIntoPdfCondition = (v: any) =>
  ((v?.files && Array.isArray(v.files) ? v.files : [])?.length ?? 0) > 1

const combineIntoPdfField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'Combine into one PDF',
  condition: combineIntoPdfCondition,
}

const fileNameField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'PDF file name',
  condition: v => combineIntoPdfCondition(v) && !!v?.combineIntoPdf,
}

const UploadFileContent = ({
  isOpen,
  onClose,
  accessLevel,
}: {
  isOpen: boolean
  onClose: () => void
  accessLevel: UserGroup
}) => {
  const { processResponse } = useContext(PopUpMessageContext)
  const {
    selectedAssessment: { practiceRef, ref },
    assessmentId,
  } = useProfile()
  const { auth } = useFirebase()

  const [shouldNotify, setShouldNotify] = useState<boolean | null>(null)

  const { appName } = useApp()
  const { admin } = useAuth()
  const [mergingFiles, setMergingFiles] = useState(false)

  const newFileId = useMemo(() => (isOpen ? Date.now() : null), [isOpen])
  const newFileField = useMemo(
    () => (newFileId && assessmentId ? getPatientFileField(`${newFileId}`) : null),
    [newFileId, assessmentId],
  )

  const newFilesField = useMemo(
    () => (newFileId && assessmentId ? getPatientFilesField(`${newFileId}`) : null),
    [assessmentId, newFileId],
  )

  const handleSubmit = useCallback(
    async (
      {
        files: data,
        fileName,
        combineIntoPdf,
        sendMessageToHb,
        messageToHb,
      }: {
        files: any[]
        combineIntoPdf: boolean
        fileName: string
        sendMessageToHb: boolean
        messageToHb: string
      },
      onUploadProgress: OnUploadProgress,
    ) => {
      const uid = auth?.currentUser?.uid
      if (!uid) return { error: 'Not logged in' }
      if (!newFileId || !newFileField || !assessmentId)
        return processResponse({ error: 'internal error' })
      const usedRef: DocumentReference<PracticeAssessmentData | Assessment> | null =
        accessLevel === 'practice' ? practiceRef : ref
      if (!usedRef) return processResponse({ error: 'internal error' })
      const willCombine = combineIntoPdf && data.length > 1
      const fileIds = data.map((_, i) => `${newFileId + i}`)
      const uploads: Record<string, UploadProgress> = {}
      try {
        const processed = (await Promise.all(
          data.map((d, i) =>
            processFileFieldData(
              appName,
              admin,
              uid,
              `${getAssessmentFilesDir(assessmentId, accessLevel)}/${fileIds[i]}`,
              [],
              newFileField,
              d,
              undefined,
              p => {
                const id = fileIds[i]
                if (id) uploads[id] = p
                onUploadProgress(uploads)
              },
            ),
          ),
        )) as FileDBValue[]
        if (!processed) return processResponse({ error: 'Error uploading files' })
        const batch = writeBatch(db)
        processed.forEach((p, i) =>
          batch.update(
            usedRef,
            `files.${fileIds[i]}`,
            addMetadata(willCombine ? { ...p, combiningIntoPdf: true } : p, appName, true),
          ),
        )
        await batch.commit()
        if (sendMessageToHb) {
          await sendThreadMessage(
            appName,
            ThreadType.ASSESSMENT,
            assessmentId,
            messageToHb || `${accessLevel === 'practice' ? 'Practice' : 'Shared'} files uploaded`,
            null,
          )
        }
        onUploadProgress({})
        if (willCombine) {
          setMergingFiles(true)
          await combineFilesIntoPdf({
            fileIds: fileIds.map(id => `practiceFiles.${id}`),
            appName,
            fileName,
            assessmentId,
          })
          setMergingFiles(false)
        }
        onClose()
        return processResponse({ success: 'Files uploaded' })
      } catch (err: any) {
        console.error(err)
        return processResponse({
          error: err?.message || 'Error uploading file',
        })
      }
    },
    [
      practiceRef,
      ref,
      onClose,
      processResponse,
      newFileId,
      appName,
      assessmentId,
      newFileField,
      auth,
      admin,
      accessLevel,
    ],
  )

  const field = useMemo<FieldMap | null>(
    () =>
      newFilesField
        ? {
            name: '',
            children: {
              files: newFilesField,
              combineIntoPdf: combineIntoPdfField,
              fileName: fileNameField,
              sendMessageToHb: {
                type: FieldTypes.CHECKBOX,
                // placeholder: 'Send message to HB',
                placeholder: 'Notify HB Admins to review',
                // condition: v => !!v?.files?.length,
              },
              messageToHb: {
                type: FieldTypes.TEXTAREA,
                placeholder: 'Message to HB',
                condition: v => !!v?.sendMessageToHb,
                optional: true,
              },
            },
          }
        : null,
    [newFilesField],
  )

  const initValue = useMemo(
    () => ({
      files: [] as FileDBValue[],
      combineIntoPdf: true,
      sendMessageToHb: !!shouldNotify,
      messageToHb: '',
      fileName: '',
    }),
    [shouldNotify],
  )
  return (
    <Flex w="100%">
      <Box w="100%">
        <ShouldNotify shouldNotify={shouldNotify} setShouldNotify={setShouldNotify} />
        {shouldNotify !== null && field && newFileId ? (
          <SimpleForm
            // ref={inputRef}
            field={field}
            value={initValue}
            boxProps={{
              bg: 'transparent',
              py: 1,
              px: 0,
              boxShadow: 'none',
            }}
            onSubmit={(val, onUploadProgress) =>
              handleSubmit(val, onUploadProgress)
                .then(res => (res.error ? { [FORM_ERROR]: res.error } : undefined))
                .catch(err =>
                  processResponse({
                    error: err?.message || 'An error occurred',
                  }),
                )
            }
          />
        ) : null}
        <Collapse in={mergingFiles} style={{ width: '100%' }}>
          <Center pt={1} pb={4} gap={2}>
            <CircularProgress size={6} isIndeterminate color="green.300" />
            <Text fontFamily="Comfortaa" fontSize="sm" color="gray.600" fontWeight={600}>
              Merging Files...
            </Text>
          </Center>
        </Collapse>
      </Box>
    </Flex>
  )
}

export const UploadFilePopover = ({
  placement = 'bottom',
  accessLevel,
}: {
  placement?: PopoverProps['placement']
  accessLevel: UserGroup
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isMobile } = useContext(ScreenContext)
  const { maxHeight, containerRef } = useProfilePopoverMaxHeight(isOpen)

  // const inputRef = useRef<InputRef>(null)

  return (
    <Box flex={isMobile ? 1 : undefined}>
      <Popover
        closeOnBlur={false}
        isOpen={isOpen}
        onClose={onClose}
        placement={placement}
        isLazy
        onOpen={onOpen}
        strategy="fixed">
        <PopoverTrigger>
          <Box w={isMobile ? '100%' : undefined}>
            <Button
              size={isMobile ? 'xs' : 'sm'}
              fontWeight={300}
              w={isMobile ? '100%' : undefined}
              fontFamily="hero-new"
              bg={colors.green.hex}
              color="gray.800"
              boxShadow="md"
              _hover={{ bg: 'green.300' }}>
              <HStack spacing={0}>
                <Image width="18px" src={uploadIcon} filter="grayscale(100%) brightness(200%)" />
                <Text
                  height="14px"
                  fontFamily="Comfortaa"
                  px={1}
                  fontSize={isMobile ? 'xs' : 'sm'}
                  fontWeight={600}
                  color="gray.50">
                  FILE
                </Text>
              </HStack>
            </Button>
          </Box>
        </PopoverTrigger>
        <Portal>
          <PopoverContent w="auto">
            <PopoverHeader
              bg={colors.green.hex}
              borderTopRadius={6}
              fontFamily="Comfortaa"
              py={1.5}
              fontWeight={600}
              color="white"
              textShadow="1px 1px 3px #00000066">
              Upload Files
            </PopoverHeader>
            <PopoverBody
              bg="gray.50"
              borderRadius={6}
              ref={containerRef}
              display="flex"
              flexFlow="column"
              w="450px"
              maxW="100vw"
              maxH={`${maxHeight}px`}
              h="auto"
              overflowY="auto"
              p={0}>
              <PopoverCloseButton top={2} borderRadius="full" bg="blackAlpha.300" color="white" />
              <UploadFileContent isOpen={isOpen} onClose={onClose} accessLevel={accessLevel} />
            </PopoverBody>
            <PopoverArrow bg={placement.startsWith('bottom') ? colors.green.hex : 'gray.50'} />
          </PopoverContent>
        </Portal>
      </Popover>
    </Box>
  )
}

export const RightUploadFilePopover = ({ accessLevel }: { accessLevel: UserGroup }) => (
  <Box px={1}>
    <UploadFilePopover placement="right" accessLevel={accessLevel} />
  </Box>
)
